import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiBell, FiMoon, FiSun, FiSave, FiEdit, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../common/ToastProvider';
import apiService from '../../services/api';

const SettingsContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  margin: 0;
`;

const SettingsSection = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.25rem, 2vw + 1rem, 1.5rem);
  font-weight: 500;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &:disabled {
    background-color: var(--background-secondary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 0.875rem;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 0.75rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 0.875rem 3rem 0.875rem 0.875rem;
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: var(--text-muted);
    cursor: not-allowed;
    transform: none;
  }
  
  &.secondary {
    background-color: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-tertiary);
      border-color: var(--primary-color);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.div`
  flex: 1;
`;

const ToggleTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
  margin: 0 0 0.25rem 0;
`;

const ToggleDescription = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: var(--primary-color);
  }
  
  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-color);
  transition: 0.3s;
  border-radius: 0;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: var(--background-primary);
    transition: 0.3s;
    border-radius: 0;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-weight: 600;
  font-size: 1.5rem;
  font-family: 'Inter', sans-serif;
  background-image: ${props => props.src ? `url(${props.src})` : 'none'};
  background-size: cover;
  background-position: center;
`;

const AvatarUpload = styled.div`
  flex: 1;
`;

const UploadButton = styled(Button)`
  &.secondary {
    margin-top: 0.5rem;
  }
`;

function Settings() {
  const { user, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const toast = useToast();
  const [profileData, setProfileData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: true,
    assignmentReminders: true,
    gradeNotifications: true,
    courseUpdates: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: ''
      });
    }
  }, [user]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleProfileChange = (field, value) => {
    // Format phone number if it's the phone field
    const formattedValue = field === 'phone' ? formatPhoneNumber(value) : value;
    setProfileData({ ...profileData, [field]: formattedValue });
  };

  const handleProfileUpdate = async () => {
    try {
      // Strip phone formatting before sending to backend
      const phoneDigits = profileData.phone ? profileData.phone.replace(/\D/g, '') : '';
      
      // Validate phone if provided (must be exactly 10 digits or empty)
      if (phoneDigits && phoneDigits.length !== 10) {
        toast.error('Phone number must contain exactly 10 digits');
        return;
      }

      const updateData = {
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        phone: phoneDigits || null
      };

      const response = await apiService.updateUserProfile(updateData);
      
      if (response.success) {
        // Update local user state
        updateUser(response.user);
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      } else {
        toast.error('Failed to update profile: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    try {
      const response = await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.success) {
        toast.success('Password updated successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Reset password visibility states
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });
      } else {
        toast.error('Failed to change password: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password. Please try again.');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);

    try {
      // Upload file
      const uploadResponse = await apiService.uploadFile(file, 'avatar');
      
      if (uploadResponse.success && uploadResponse.file?.url) {
        // Construct full URL for the avatar
        const fileUrl = uploadResponse.file.url;
        const avatarUrl = fileUrl.startsWith('http') 
          ? fileUrl 
          : `${window.location.origin}${fileUrl}`;
        
        // Update profile with avatar URL
        const updateResponse = await apiService.updateUserProfile({
          avatarUrl: avatarUrl
        });

        if (updateResponse.success) {
          // Update local user state
          updateUser(updateResponse.user);
          toast.success('Avatar updated successfully!');
        } else {
          toast.error('Failed to update avatar: ' + (updateResponse.error || 'Unknown error'));
        }
      } else {
        toast.error('Failed to upload image: ' + (uploadResponse.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const triggerFileInput = () => {
    document.getElementById('avatar-upload-input')?.click();
  };

  return (
    <SettingsContainer>
      <Header>
        <Title>Settings</Title>
        <Subtitle>Manage your account preferences and settings</Subtitle>
      </Header>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <SectionHeader>
          <SectionIcon>
            <FiUser size={20} />
          </SectionIcon>
          <SectionTitle>Profile Information</SectionTitle>
        </SectionHeader>

        <AvatarSection>
          <Avatar src={user?.avatar_url || user?.avatar}>
            {!user?.avatar_url && !user?.avatar && getInitials(profileData.firstName, profileData.lastName)}
          </Avatar>
          <AvatarUpload>
            <Label>Profile Picture</Label>
            <input
              id="avatar-upload-input"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
            <UploadButton 
              className="secondary" 
              onClick={triggerFileInput}
              disabled={uploadingAvatar}
            >
              <FiEdit size={16} />
              {uploadingAvatar ? 'Uploading...' : 'Upload Image'}
            </UploadButton>
          </AvatarUpload>
        </AvatarSection>

        <FormGroup>
          <Label>First Name</Label>
          <Input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
            disabled={!isEditing}
          />
        </FormGroup>

        <FormGroup>
          <Label>Last Name</Label>
          <Input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
            disabled={!isEditing}
          />
        </FormGroup>

        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            value={profileData.email}
            disabled
          />
        </FormGroup>

        <FormGroup>
          <Label>Phone Number</Label>
          <Input
            type="tel"
            value={profileData.phone}
            onChange={(e) => handleProfileChange('phone', e.target.value)}
            disabled={!isEditing}
            placeholder="(xxx) xxx-xxxx"
            maxLength={14}
          />
        </FormGroup>

        <FormGroup>
          <Label>Bio</Label>
          <TextArea
            value={profileData.bio}
            onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
            disabled={!isEditing}
            placeholder="Tell us about yourself..."
          />
        </FormGroup>

        <ButtonGroup>
          {isEditing ? (
            <>
              <Button onClick={handleProfileUpdate}>
                <FiSave size={16} />
                Save Changes
              </Button>
              <Button className="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <FiEdit size={16} />
              Edit Profile
            </Button>
          )}
        </ButtonGroup>
      </SettingsSection>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <SectionHeader>
          <SectionIcon>
            <FiLock size={20} />
          </SectionIcon>
          <SectionTitle>Security</SectionTitle>
        </SectionHeader>

        <FormGroup>
          <Label>Current Password</Label>
          <PasswordInputWrapper>
            <PasswordInput
              type={showPasswords.current ? "text" : "password"}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            />
            <PasswordToggleButton
              type="button"
              onClick={() => togglePasswordVisibility('current')}
            >
              {showPasswords.current ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </PasswordToggleButton>
          </PasswordInputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>New Password</Label>
          <PasswordInputWrapper>
            <PasswordInput
              type={showPasswords.new ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            />
            <PasswordToggleButton
              type="button"
              onClick={() => togglePasswordVisibility('new')}
            >
              {showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </PasswordToggleButton>
          </PasswordInputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Confirm New Password</Label>
          <PasswordInputWrapper>
            <PasswordInput
              type={showPasswords.confirm ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            />
            <PasswordToggleButton
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
            >
              {showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </PasswordToggleButton>
          </PasswordInputWrapper>
        </FormGroup>

        <Button onClick={handlePasswordChange}>
          <FiLock size={16} />
          Change Password
        </Button>
      </SettingsSection>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <SectionHeader>
          <SectionIcon>
            <FiBell size={20} />
          </SectionIcon>
          <SectionTitle>Notifications</SectionTitle>
        </SectionHeader>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Email Notifications</ToggleTitle>
            <ToggleDescription>Receive notifications via email</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={notifications.emailNotifications}
              onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Push Notifications</ToggleTitle>
            <ToggleDescription>Receive browser push notifications</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={notifications.pushNotifications}
              onChange={(e) => setNotifications({ ...notifications, pushNotifications: e.target.checked })}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Assignment Reminders</ToggleTitle>
            <ToggleDescription>Get reminded about upcoming assignments</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={notifications.assignmentReminders}
              onChange={(e) => setNotifications({ ...notifications, assignmentReminders: e.target.checked })}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Grade Notifications</ToggleTitle>
            <ToggleDescription>Get notified when grades are posted</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={notifications.gradeNotifications}
              onChange={(e) => setNotifications({ ...notifications, gradeNotifications: e.target.checked })}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Course Updates</ToggleTitle>
            <ToggleDescription>Receive updates about your courses</ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={notifications.courseUpdates}
              onChange={(e) => setNotifications({ ...notifications, courseUpdates: e.target.checked })}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>
      </SettingsSection>

      <SettingsSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <SectionHeader>
          <SectionIcon>
            {theme === 'light' ? <FiSun size={20} /> : <FiMoon size={20} />}
          </SectionIcon>
          <SectionTitle>Appearance</SectionTitle>
        </SectionHeader>

        <ToggleContainer>
          <ToggleLabel>
            <ToggleTitle>Dark Mode</ToggleTitle>
            <ToggleDescription>
              {theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}
            </ToggleDescription>
          </ToggleLabel>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <ToggleSlider />
          </ToggleSwitch>
        </ToggleContainer>
      </SettingsSection>
    </SettingsContainer>
  );
}

export default Settings;
